import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { apiFetch } from '../api/client';
import { forumThreadUrl } from '../lib/slugId';
import { useAuth } from '../auth/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { LogCard } from '../components/LogCard';
import { SectionLayout } from '../components/SectionLayout';
import { SectionNav } from '../components/SectionNav';
import { SectionHeader } from '../components/SectionHeader';
import { NewActivityModal } from '../components/NewActivityModal';
import { recordGarageVisit } from '../garage/garageVisits';
import { GoalsSection } from '../garage/goals/GoalsSection';
import {
  GARAGE_SECTION_GROUPS,
  isValidGarageSection,
  type GarageSectionKey,
} from '../garage/sections';
import type {
  Activity,
  Car,
  CarContributor,
  Conversation,
  ForumThread,
  Paginated,
  SingleResponse,
} from '../api/types';

export function GarageCarDashboard() {
  const { slugId } = useParams<{ slugId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [car, setCar] = useState<Car | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const tabParam = searchParams.get('tab');
  const currentTab: GarageSectionKey = isValidGarageSection(tabParam) ? tabParam : 'goals';

  const handleDelete = async () => {
    if (!car) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await apiFetch(`/api/v1/cars/${car.slug}-${car.id}`, { method: 'DELETE' });
      setConfirmOpen(false);
      navigate('/garage', { replace: true });
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : t('garageCar.details.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!slugId || !user) return;
    apiFetch<SingleResponse<Car>>(`/api/v1/cars/${slugId}`)
      .then((res) => {
        // Non-owners bounce to the public car page.
        if (res.data.user_id !== user.id) {
          navigate(`/cars/${slugId}`, { replace: true });
          return;
        }
        setCar(res.data);
        recordGarageVisit(res.data.garage_id);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slugId, user, navigate]);

  const handleSectionChange = (key: string) => {
    setSearchParams({ tab: key }, { replace: true });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !car) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <Alert severity="error">{error || t('carDetail.notFound')}</Alert>
      </Container>
    );
  }

  const sidebarHeader = (
    <Stack spacing={1.5}>
      {car.photo_urls[0] && (
        <Box
          component="img"
          src={car.photo_urls[0]}
          alt={car.display_name}
          sx={{
            width: '100%',
            height: 120,
            objectFit: 'cover',
            borderRadius: 1,
          }}
        />
      )}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="h6" noWrap>
            {car.display_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {car.year} {car.make} {car.model}
          </Typography>
        </Box>
        <Tooltip title={t('garageCar.details.edit')}>
          <IconButton
            size="small"
            component={RouterLink}
            to={`/cars/${car.slug}-${car.id}/edit`}
            aria-label={t('garageCar.details.edit')}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );

  const sidebarFooter = (
    <Button
      size="small"
      variant="text"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => setConfirmOpen(true)}
      sx={{ justifyContent: 'flex-start', width: '100%' }}
    >
      {t('garageCar.details.delete')}
    </Button>
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader
        crumbs={[
          { label: t('nav.garage'), to: '/garage' },
          { label: car.display_name },
        ]}
      />

      <SectionLayout
        mobileNavLabel={t('garageCar.sectionsMenu')}
        sidebar={
          <SectionNav
            groups={GARAGE_SECTION_GROUPS}
            value={currentTab}
            onChange={handleSectionChange}
            header={sidebarHeader}
            footer={sidebarFooter}
          />
        }
      >
        {currentTab === 'activities' && <ActivitiesTab car={car} />}
        {currentTab === 'goals' && <GoalsSection car={car} />}
        {currentTab === 'messages' && <MessagesTab car={car} />}
        {currentTab === 'threads' && <ThreadsTab car={car} />}
        {currentTab === 'contributors' && <ContributorsTab car={car} />}
      </SectionLayout>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t('garageCar.details.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('garageCar.details.confirmDelete', { nickname: car.display_name })}
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t('common.cancel')}</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

// ─── Activities tab ────────────────────────────────────────────────────────

function ActivitiesTab({ car }: { car: Car }) {
  const { t } = useTranslation();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<Paginated<Activity>>(
        `/api/v1/activities?filter[car_id]=${car.id}&sort=-scheduled_at&per_page=50`
      );
      setActivities(res.data);
    } finally {
      setLoading(false);
    }
  }, [car.id]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('garageCar.activities.deleteConfirm'))) return;
    setDeletingId(id);
    try {
      await apiFetch(`/api/v1/activities/${id}`, { method: 'DELETE' });
      setActivities((prev) => prev.filter((a) => a.id !== id));
    } catch {
      window.alert(t('garageCar.activities.deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreated = async () => {
    setModalOpen(false);
    await refetch();
  };

  return (
    <Stack spacing={3}>
      <SectionHeader
        title={t('garageCar.tabs.activities')}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
          >
            {t('garageCar.activities.newButton')}
          </Button>
        }
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : activities.length === 0 ? (
        <Typography color="text.secondary">{t('garageCar.activities.empty')}</Typography>
      ) : (
        <Stack spacing={2}>
          {activities.map((act) => (
            <Box key={act.id} sx={{ position: 'relative' }}>
              <LogCard
                date={act.scheduled_at ?? act.created_at}
                title={act.summary}
                chip={t(`activityType.${act.type}`, { defaultValue: act.type_label })}
                location={act.location_name ?? undefined}
              />
              <IconButton
                size="small"
                onClick={() => handleDelete(act.id)}
                disabled={deletingId === act.id}
                aria-label={t('common.delete')}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      <NewActivityModal
        open={modalOpen}
        car={car}
        onClose={() => setModalOpen(false)}
        onCreated={handleCreated}
      />
    </Stack>
  );
}

// ─── Messages tab ──────────────────────────────────────────────────────────

function MessagesTab({ car }: { car: Car }) {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<Paginated<Conversation>>(
      `/api/v1/conversations?filter[car_id]=${car.id}&per_page=50`
    )
      .then((res) => setConversations(res.data))
      .finally(() => setLoading(false));
  }, [car.id]);

  return (
    <Stack spacing={3}>
      <SectionHeader title={t('garageCar.tabs.messages')} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : conversations.length === 0 ? (
        <Typography color="text.secondary">{t('garageCar.messages.empty')}</Typography>
      ) : (
        <Stack spacing={1.5}>
          {conversations.map((c) => (
            <Card key={c.id} variant="outlined">
              <CardActionArea component={RouterLink} to={`/inbox/${c.id}`}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar src={c.other_participant?.avatar_url ?? undefined}>
                      {c.other_participant?.name?.[0] ?? '?'}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                      >
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {c.other_participant?.name ?? '—'}
                        </Typography>
                        {c.unread_count > 0 && (
                          <Chip
                            size="small"
                            color="error"
                            label={t('inbox.unread', { count: c.unread_count })}
                          />
                        )}
                      </Stack>
                      {c.latest_message && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            mt: 0.5,
                          }}
                        >
                          {c.latest_message.body}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

// ─── Threads tab ───────────────────────────────────────────────────────────

function ThreadsTab({ car }: { car: Car }) {
  const { t } = useTranslation();
  const [threads, setThreads] = useState<ForumThread[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<Paginated<ForumThread>>(
      `/api/v1/forum/threads?filter[car_id]=${car.id}&include=user,category&per_page=50`
    )
      .then((res) => setThreads(res.data))
      .finally(() => setLoading(false));
  }, [car.id]);

  return (
    <Stack spacing={3}>
      <SectionHeader
        title={t('garageCar.tabs.threads')}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            component={RouterLink}
            to={`/forum/new?carId=${car.id}`}
          >
            {t('garageCar.threads.newButton')}
          </Button>
        }
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : threads.length === 0 ? (
        <Typography color="text.secondary">{t('garageCar.threads.empty')}</Typography>
      ) : (
        <Stack spacing={1.5}>
          {threads.map((thread) => (
            <Card key={thread.id} variant="outlined">
              <CardActionArea component={RouterLink} to={forumThreadUrl(thread)}>
                <CardContent>
                  <Stack spacing={0.75}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {thread.title}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
                      {thread.category && (
                        <Chip size="small" label={thread.category.name} variant="outlined" />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {thread.last_post_at ? format(new Date(thread.last_post_at), 'yyyy-MM-dd HH:mm') : ''}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

// ─── Contributors tab ──────────────────────────────────────────────────────

function ContributorsTab({ car }: { car: Car }) {
  const { t } = useTranslation();
  const [contributors, setContributors] = useState<CarContributor[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ data: CarContributor[] }>(
        `/api/v1/cars/${car.id}/contributors`
      );
      setContributors(res.data);
    } finally {
      setLoading(false);
    }
  }, [car.id]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleRespond = async (contributor: CarContributor, action: 'approve' | 'deny') => {
    setBusyId(contributor.user_id);
    try {
      const res = await apiFetch<SingleResponse<CarContributor>>(
        `/api/v1/cars/${car.id}/contributors/${contributor.user_id}/${action}`,
        { method: 'POST' }
      );
      setContributors((prev) =>
        prev.map((c) => (c.user_id === contributor.user_id ? res.data : c))
      );
    } finally {
      setBusyId(null);
    }
  };

  const pending = contributors.filter((c) => c.status === 'pending');
  const approved = contributors.filter((c) => c.status === 'approved');
  const denied = contributors.filter((c) => c.status === 'denied');

  const row = (c: CarContributor) => (
    <Card key={c.id} variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar src={c.user?.avatar_url ?? undefined}>
            {c.user?.name?.[0] ?? '?'}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {c.user?.name ?? '—'}
            </Typography>
            {(c.user?.city || c.user?.country) && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {[c.user?.city, c.user?.country].filter(Boolean).join(', ')}
              </Typography>
            )}
          </Box>
          {c.status === 'pending' && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => handleRespond(c, 'approve')}
                disabled={busyId === c.user_id}
              >
                {t('garageCar.contributors.approve')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleRespond(c, 'deny')}
                disabled={busyId === c.user_id}
              >
                {t('garageCar.contributors.deny')}
              </Button>
            </Stack>
          )}
          {c.status === 'approved' && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" color="success" label={t('contribute.statusApproved')} />
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleRespond(c, 'deny')}
                disabled={busyId === c.user_id}
              >
                {t('garageCar.contributors.revoke')}
              </Button>
            </Stack>
          )}
          {c.status === 'denied' && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={t('contribute.statusDenied')} />
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={() => handleRespond(c, 'approve')}
                disabled={busyId === c.user_id}
              >
                {t('garageCar.contributors.approve')}
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Stack spacing={4}>
      <SectionHeader title={t('garageCar.tabs.contributors')} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : contributors.length === 0 ? (
        <Typography color="text.secondary">{t('garageCar.contributors.empty')}</Typography>
      ) : (
        <Stack spacing={4}>
          {pending.length > 0 && (
            <Stack spacing={1.5}>
              <Typography variant="overline" color="text.secondary">
                {t('garageCar.contributors.pendingSection')}
              </Typography>
              {pending.map(row)}
            </Stack>
          )}
          {approved.length > 0 && (
            <Stack spacing={1.5}>
              <Typography variant="overline" color="text.secondary">
                {t('garageCar.contributors.approvedSection')}
              </Typography>
              {approved.map(row)}
            </Stack>
          )}
          {denied.length > 0 && (
            <Stack spacing={1.5}>
              <Typography variant="overline" color="text.secondary">
                {t('garageCar.contributors.deniedSection')}
              </Typography>
              {denied.map(row)}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}

